/*
 * Runs the vendor engine's BlogPage handler on translated blog URLs.
 *
 * THE BUG THIS FIXES. The engine's router (`vendor/rise-webflow/src/routes.ts`) registers
 * `'/blog/*': BlogPage`, and @sygnal/sse matches a wildcard route with
 * `path.startsWith(baseRoute)` — literally `'/blog/'`. A Spanish post lives at
 * `/es/blog/<slug>`, which does not start with `/blog/`, so `matchRoute()` returned null and the
 * route-level handler never ran on any Spanish page.
 *
 * Site-level setup/exec were unaffected (they are not route-gated), which is exactly why this was
 * invisible in the HTML and in every harness: the markup is byte-correct, and the page looks right
 * until you notice what is not wired up. Everything BlogPage.exec() owns was dead on all 15 posts:
 *
 *   Plyr                     the video player never initialised, leaving an empty `.video-player`
 *                            div at its CSS aspect-ratio height — a ~400px blank gap under the
 *                            byline on the 9 posts that have a video. This is how it was spotted.
 *   Sa5SocialShareController the Facebook / X / LinkedIn / email share buttons did nothing
 *   Linkify                  the `rise-linkify-*` attributes this template sets were never
 *                            processed, so in-body links were not rewritten to Adjust deep links.
 *                            Conversion-relevant, and the quietest of the failures.
 *   TocFix                   table-of-contents anchors and click handlers
 *   AdjustTrackingQr         the desktop QR code for app install
 *   GTMEvent                 social-share event tracking
 *
 * WHY THE FIX IS HERE AND NOT IN routes.ts. `vendor/rise-webflow/src` is imported verbatim and is
 * deliberately never edited: `scripts/build-engine.mjs --verify` diffs our bundle's module list
 * against production's Netlify sourcemap, entry for entry, and that comparison is only meaningful
 * while the vendored source is pristine (docs/ENGINE.md). Adding a route there would buy a
 * one-line diff at the cost of the guarantee that our engine is production's engine. So the
 * dispatch is extended from our own side instead.
 *
 * Timing mirrors vendor/rise-webflow/src/index.ts exactly — `setup()` synchronously at module
 * evaluation, `exec()` immediately if the DOM is already parsed and on DOMContentLoaded otherwise.
 * src/lib/engine.ts imports this AFTER the vendor engine so the vendor's own setup() has already
 * run, matching the order the router would have used.
 */
import { BlogPage } from '../../vendor/rise-webflow/src/page/blog';
import { LOCALES } from './locale';

/*
 * Derived from the locale table rather than hardcoded, so a new language picks this up by existing.
 * English is excluded because `/blog/*` is the router's own route and already dispatches correctly —
 * running BlogPage twice there would double every event listener.
 *
 * `/de/blog/*` is NOT in this list today: those ten pages are hand-authored HTML partials, they have
 * never had the route handler, and switching it on for them is a live behaviour change to German that
 * belongs with the German migration rather than smuggled into a Spanish fix. Adding `de` to
 * src/lib/locale.ts is what will enable it, deliberately.
 */
const TRANSLATED_BLOG_PREFIXES = Object.values(LOCALES)
  .filter((l) => l.collection !== 'blog')
  .map((l) => `${l.base}/`);

const path = window.location.pathname;

if (TRANSLATED_BLOG_PREFIXES.some((prefix) => path.startsWith(prefix))) {
  const page = new BlogPage();
  page.setup();

  const exec = () => page.exec();
  if (document.readyState !== 'loading') exec();
  else document.addEventListener('DOMContentLoaded', exec);
}
